import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { FileSearch, Upload } from 'lucide-react';
import { StatusChip } from '../abf/components/StatusChip';
import { Note, SmallButton } from '../abf/onboarding/review/contractReviewStyles';
import { type DryRun, type DryRunField, type DryRunSummary, getDryRun, listDryRuns, startDryRun, templateErrorMessage } from './documentTemplatesApi';
import { showValue } from './templateModel';

const Start = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin-bottom: 16px;
  input[type='file'] { color: #c5d4e6; font-size: 13px; }
`;

const Summary = styled.div`
  display: flex; flex-wrap: wrap; gap: 10px 24px; align-items: center; margin-bottom: 14px; color: #c5d4e6; font-size: 13px;
  strong { color: #edf4ff; }
`;

const Table = styled.section`
  border: 1px solid #29374b; border-radius: 10px; background: #0c1524; margin-bottom: 16px; overflow: hidden;
  > h3 { padding: 12px 16px; background: #0f1b2b; border-bottom: 1px solid #29374b; font-size: 14px; }
  .line { display: grid; grid-template-columns: minmax(180px, 1fr) minmax(160px, 1fr) minmax(0, 2fr); gap: 14px; padding: 11px 16px; }
  .line + .line { border-top: 1px solid #1f2b3a; }
  .line[data-error='true'] { background: #2a1717; }
  .line[data-unstated='true'] .value { color: #7d8ea3; }
  .value { font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  small { display: block; color: #91a5bd; font-size: 12px; margin-top: 3px; }
  blockquote { margin: 0; color: #b6c7da; font-size: 13px; border-left: 2px solid #33465e; padding-left: 10px; }
  .error { color: #fca5a5; font-size: 12px; margin-top: 4px; }
  @media (max-width: 1000px) { .line { grid-template-columns: 1fr; } }
`;

const Changes = styled.ul`
  margin: 0 0 16px; padding: 0; list-style: none;
  li { padding: 8px 12px; border: 1px solid #36485e; border-radius: 7px; margin-bottom: 6px; font-size: 13px; color: #c5d4e6; }
  code { color: #e5edf7; }
`;

const POLL_MS = 3000;

const grouped = (fields: DryRunField[]) => {
  const out = new Map<string, DryRunField[]>();
  fields.forEach(field => {
    const group = field.review_group || 'Other';
    out.set(group, [...(out.get(group) || []), field]);
  });
  return Array.from(out.entries());
};

/**
 * Try a version on a sample document before anyone relies on it: the document is read with this
 * version exactly as a job would read it. Nothing is booked and no job is created.
 */
const DryRunPanel: React.FC<{ versionId: number; version: number; blockedReason: string | null }> = ({
  versionId, version, blockedReason,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [run, setRun] = useState<DryRun | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);

  const poll = (runId: string) => {
    timer.current = window.setTimeout(async () => {
      try {
        const latest = await getDryRun(runId);
        setRun(latest);
        if (latest.status === 'PROCESSING') poll(runId); else void loadHistory();
      } catch (failure) {
        setError(templateErrorMessage(failure, 'The run could not be read back'));
      }
    }, POLL_MS);
  };

  // F-PCP-057: the reads tried with this version, newest first; a reload must not lose the read in progress — the
  // agent lists it first and the page follows it again — and every finished read opens with its full result.
  const [history, setHistory] = useState<DryRunSummary[] | null>(null);
  const loadHistory = () => listDryRuns(versionId).then(setHistory).catch(() => setHistory([]));
  useEffect(() => {
    let alive = true;
    listDryRuns(versionId).then(runs => {
      if (!alive) return;
      setHistory(runs);
      const live = runs.find(r => r.status === 'PROCESSING');
      if (live) {
        setRun({ run_id: live.run_id, status: 'PROCESSING', document_name: live.document_name, started_at: live.started_at } as DryRun);
        poll(live.run_id);
      }
    }).catch(() => { if (alive) setHistory([]); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionId]);
  const openRun = async (runId: string) => {
    setError(null);
    try { setRun(await getDryRun(runId)); }
    catch (failure) { setError(templateErrorMessage(failure, 'The run could not be read back')); }
  };

  const start = async () => {
    if (!file) return;
    setStarting(true);
    setError(null);
    setRun(null);
    try {
      const started = await startDryRun(versionId, file);
      setRun({ run_id: started.run_id, status: 'PROCESSING', document_name: file.name, started_at: new Date().toISOString() } as DryRun);
      poll(started.run_id);
    } catch (failure) {
      setError(templateErrorMessage(failure, 'The document could not be read'));
    } finally {
      setStarting(false);
    }
  };

  const groups = useMemo(() => grouped(run?.fields || []), [run]);
  const errorCount = run?.validation?.error_count || 0;
  const unstatedRequired = (run?.fields || []).filter(f => f.required && !f.stated).length;
  const statedCount = (run?.fields || []).filter(f => f.stated).length;

  return (
    <div>
      {history !== null && <Note role="status" style={{ marginBottom: 12 }}>
        <strong>Reads tried with this version</strong>
        {history.length === 0 ? <p style={{ margin: '4px 0 0' }}>None recorded yet. A checker activates a version on the strength of the reads listed here.</p>
          : <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>{history.map(r => <li key={r.run_id} style={{ margin: '4px 0' }}>
              {r.document_name || r.run_id} · {r.status === 'PROCESSING' ? 'reading' : r.status.toLowerCase()}
              {r.validation_status ? ` · rules ${r.validation_status}${r.error_count ? ` (${r.error_count} to check)` : ''}` : ''}
              {r.booking_changes != null ? ` · ${r.booking_changes} booking change(s)` : ''}
              {r.completed_at ? ` · ${new Date(r.completed_at).toLocaleString()}` : r.started_at ? ` · since ${new Date(r.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
              {r.status !== 'PROCESSING' && run?.run_id !== r.run_id && <> <SmallButton type="button" onClick={() => void openRun(r.run_id)} style={{ minHeight: 28, padding: '3px 10px', marginLeft: 8 }}>Open</SmallButton></>}
              {run?.run_id === r.run_id && <small style={{ marginLeft: 8, color: '#9bdef5' }}>shown below</small>}
            </li>)}</ul>}
      </Note>}
      <Note style={{ marginBottom: 14 }}>
        <p>
          Choose a sample PDF. It is read with version {version} the way a job would read it: the model reads the
          document, so this takes a few minutes. Nothing is booked, no job is created and the file is not kept.
        </p>
      </Note>
      {blockedReason ? (
        <Note role="status">{blockedReason}</Note>
      ) : (
        <Start>
          <input type="file" accept="application/pdf,.pdf" aria-label="Sample document"
            onChange={event => setFile(event.target.files?.[0] || null)} />
          <SmallButton type="button" onClick={() => void start()}
            disabled={!file || starting || run?.status === 'PROCESSING'} style={{ borderColor: '#67c6f4' }}>
            <Upload size={14} /> Read it with version {version}
          </SmallButton>
        </Start>
      )}
      {error && <Note role="alert" style={{ marginBottom: 14, borderColor: '#7f3b3b' }}>{error}</Note>}

      {run?.status === 'PROCESSING' && (
        <Summary role="status"><StatusChip tone="progress" label="Reading" size="md" /> {run.document_name}
          {run.started_at && <> · since {new Date(run.started_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            {(() => { const min = Math.max(0, Math.round((Date.now() - new Date(run.started_at).getTime()) / 60000)); return min ? ` · ${min} min so far` : ''; })()}</>}
          {' '}· the model reads the whole document; a read takes 5–10 minutes and survives a page reload.</Summary>
      )}
      {run?.status === 'FAILED' && (
        <Note role="alert" style={{ borderColor: '#7f3b3b' }}>
          <strong>The read failed.</strong> <p>{run.error}</p>
        </Note>
      )}
      {run?.status === 'COMPLETED' && (
        <>
          <Summary>
            <span><FileSearch size={14} style={{ verticalAlign: -2 }} /> <strong>{run.document_name}</strong></span>
            {errorCount === 0
              ? <StatusChip tone="done" label="Every value passes the template's rules" size="md" />
              : <StatusChip tone="critical" label={`${errorCount} value(s) break a rule`} size="md" />}
            {unstatedRequired > 0 && <StatusChip tone="warning" label={`${unstatedRequired} required field(s) not stated`} size="md" />}
            {run.manager_decision?.decision && <span>Reviewer agent: <strong>{run.manager_decision.decision.replace(/_/g, ' ').toLowerCase()}</strong></span>}
          </Summary>

          <h3 style={{ fontSize: 14, margin: '6px 0 8px' }}>What would book differently from today</h3>
          {(run.booking_changes || []).length === 0 ? (
            <Note style={{ marginBottom: 16 }}>Nothing. This version books exactly what the built-in instructions book for this document.</Note>
          ) : (
            <Changes>
              {(run.booking_changes || []).map(change => (
                <li key={change.key}>
                  <code>{change.key}</code>: {change.change === 'ADDED' ? 'now booked as ' : change.change === 'REMOVED' ? 'no longer booked (was ' : 'from '}
                  {change.change === 'CHANGED' && <>{showValue(change.code_path)} to </>}
                  {change.change === 'REMOVED' ? <>{showValue(change.code_path)})</> : showValue(change.template)}
                </li>
              ))}
            </Changes>
          )}

          <Note style={{ marginBottom: 12 }}>
            {statedCount} of {(run.fields || []).length} fields are stated in this document; the rest are reference the document type never carries and sit folded under each group.
            {' '}Passages are located when a job reads a document; a read does not cite, so "cited on a job" below is expected here.
          </Note>
          {groups.map(([group, fields]) => (
            <Table key={group} aria-label={group}>
              <h3>{group}</h3>
              {fields.filter(f => f.stated || f.required || f.errors.length > 0).map(field => (
                <div className="line" key={field.path} data-error={field.errors.length > 0} data-unstated={!field.stated}>
                  <div>
                    {field.label}
                    <small>{field.booking_key ? `Books into ${field.booking_key}` : 'Read only'}{field.required ? ' · must be stated' : ''}</small>
                  </div>
                  <div className="value">
                    {field.rows !== null ? `${field.rows} ${field.rows === 1 ? 'row' : 'rows'}` : showValue(field.value)}
                    {field.errors.map(e => (
                      <div className="error" key={e.pointer + e.rule}>
                        {e.message}{e.source === 'APPENDIX' ? ' (from the file\'s own data)' : ''}
                      </div>
                    ))}
                  </div>
                  <div>
                    {field.citation?.excerpt
                      ? <blockquote>{field.citation.excerpt}<small>Page {field.citation.page ?? '?'}{field.citation.section ? ` · ${field.citation.section}` : ''}</small></blockquote>
                      : <small>{field.stated ? (field.citation_rule === 'REQUIRED' ? 'Cited on a job (required)' : 'No passage cited') : ''}</small>}
                  </div>
                </div>
              ))}
              {fields.some(f => !(f.stated || f.required || f.errors.length > 0)) && (
                <details>
                  <summary style={{ cursor: 'pointer', color: '#91a5bd', fontSize: 12, padding: '6px 0' }}>
                    Show the {fields.filter(f => !(f.stated || f.required || f.errors.length > 0)).length} field(s) this document does not state
                  </summary>
                  {fields.filter(f => !(f.stated || f.required || f.errors.length > 0)).map(field => (
                    <div className="line" key={field.path} data-unstated="true">
                      <div>{field.label}<small>{field.booking_key ? `Books into ${field.booking_key}` : 'Read only'}</small></div>
                      <div className="value">Not stated</div>
                      <div />
                    </div>
                  ))}
                </details>
              )}
            </Table>
          ))}
        </>
      )}
    </div>
  );
};

export default DryRunPanel;
